/**
 * Graph performance instrumentation, gated behind ?perfLog=true.
 *
 * Adds performance.mark / performance.measure pairs and lightweight counters
 * across the Cytoscape rendering path. When the flag is off every helper is
 * a no-op (single URLSearchParams check + early return).
 *
 * Counters and reset are exposed on `window.__graphPerf` for ad-hoc inspection
 * from devtools.
 */

let _enabledChecked = false;
let _enabled = false;
const checkEnabled = () => {
  if (typeof window === 'undefined') return false;
  if (_enabledChecked) return _enabled;
  try {
    _enabled = new URLSearchParams(window.location.search).get('perfLog') === 'true';
  } catch (e) {
    _enabled = false;
  }
  _enabledChecked = true;
  return _enabled;
};

/** Re-evaluate the flag (useful for tests that mutate window.location). */
export const _resetPerfFlagCache = () => {
  _enabledChecked = false;
  _enabled = false;
};

export const isPerfLogEnabled = () => checkEnabled();

export const perfMark = (name) => {
  if (!checkEnabled()) return;
  try {
    performance.mark(name);
  } catch (e) {
    // ignore
  }
};

/**
 * Measure between startMark and endMark. If endMark is omitted, marks "now"
 * with the supplied name + ":end" suffix and measures from startMark to it.
 * Detail (when supported by the browser) is attached for richer DevTools view.
 */
export const perfMeasure = (name, startMark, endMark, detail) => {
  if (!checkEnabled()) return;
  const stop = endMark || `${name}:end`;
  try {
    if (!endMark) performance.mark(stop);
    performance.measure(name, { start: startMark, end: stop, detail });
  } catch (e) {
    try {
      performance.measure(name, startMark, stop);
    } catch (e2) {
      // ignore
    }
  }
};

const counters = new Map();

export const perfCount = (name, delta = 1) => {
  if (!checkEnabled()) return;
  counters.set(name, (counters.get(name) || 0) + delta);
};

export const perfCounters = () => {
  const out = {};
  counters.forEach((v, k) => { out[k] = v; });
  return out;
};

export const perfResetCounters = () => {
  counters.clear();
};

/**
 * Convenience: record a duration in ms under a counter (e.g. "transform.totalMs").
 */
export const perfAddDuration = (name, ms) => {
  if (!checkEnabled()) return;
  counters.set(name, (counters.get(name) || 0) + ms);
};

if (typeof window !== 'undefined' && !window.__graphPerf) {
  window.__graphPerf = {
    perfCounters,
    perfResetCounters,
    isPerfLogEnabled,
  };
}

export default {
  perfMark,
  perfMeasure,
  perfCount,
  perfCounters,
  perfResetCounters,
  perfAddDuration,
  isPerfLogEnabled,
};
